import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { AlertCircle, Check, X } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Toast.module.css';

export type ToastTone = 'success' | 'error';

type Toast = {
  id: number;
  message: string;
  tone: ToastTone;
};

type ToastApi = {
  /** Confirmation of something that worked. Dismisses itself. */
  success: (message: string) => void;
  /** Something failed. Stays roughly twice as long; it is worth reading. */
  error: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const SUCCESS_MS = 3600;
const ERROR_MS = 7000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone) => {
      nextId.current += 1;
      const id = nextId.current;
      setToasts((current) => [...current, { id, message, tone }]);
      window.setTimeout(
        () => {
          dismiss(id);
        },
        tone === 'error' ? ERROR_MS : SUCCESS_MS,
      );
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message: string) => {
        push(message, 'success');
      },
      error: (message: string) => {
        push(message, 'error');
      },
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        A single live region, outside the tree that changes. "polite" so it waits for
        a screen reader to finish its sentence. An "assertive" confirmation would
        interrupt whatever the user was actually reading.
      */}
      <div className={styles.stack} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={cx(styles.toast, styles[toast.tone])}>
            <span className={styles.icon} aria-hidden="true">
              {toast.tone === 'error' ? <AlertCircle size={16} /> : <Check size={16} />}
            </span>
            <span className={styles.message}>{toast.message}</span>
            <button
              type="button"
              className={styles.close}
              onClick={() => {
                dismiss(toast.id);
              }}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return api;
}
